/**
 * Cart Offers Utilities
 * Shared functions for all cart offer types (Buy X Get Y, Quantity Tiers, Cart Total Offers)
 * Version: 1.0.0
 */

if (!window.CartOfferUtils) {
  window.CartOfferUtils = {
    DEBUG: false,
    // Processing state to prevent duplicate requests
    isProcessing: false,
    // Shared cart fetch cache to avoid multiple offer scripts fetching /cart.js in parallel
    _cartFetchPromise: null,
    _cartCache: null,
    _cartCacheTime: 0,
    
    async waitForIdle(timeoutMs = 2500) {
      if (!this.isProcessing) return true;
      const start = Date.now();
      while (this.isProcessing && (Date.now() - start) < timeoutMs) {
        await new Promise((resolve) => setTimeout(resolve, 30));
      }
      return !this.isProcessing;
    },

    /**
     * Add a product to cart with properties
     * @param {number} variantId - The variant ID to add
     * @param {number} quantity - Quantity to add (default: 1)
     * @param {object} properties - Line item properties (e.g., { _offer_type: 'buy_x_get_y' })
     * @returns {Promise<{success: boolean, cart?: object, error?: any, reason?: string}>}
     */
    async addProduct(variantId, quantity = 1, properties = {}) {
      if (this.isProcessing) {
        const ready = await this.waitForIdle();
        if (!ready) return { success: false, reason: 'processing_timeout' };
      }

      if (!variantId) {
        console.error('[CartOfferUtils] No variant ID provided');
        return { success: false, reason: 'no_variant_id' };
      }

      if (this.DEBUG) console.log('[CartOfferUtils] Adding product:', { variantId, quantity, properties });
      this.isProcessing = true;

      try {
        // Get sections to render (matches theme's cart update behavior)
        let sectionsToBundle = [];
        document.documentElement.dispatchEvent(
          new CustomEvent('cart:grouped-sections', {
            bubbles: true,
            detail: { sections: sectionsToBundle }
          })
        );

        // Build FormData
        const formData = new FormData();
        formData.append('id', variantId);
        formData.append('quantity', quantity);

        // Add properties
        Object.entries(properties).forEach(([key, value]) => {
          formData.append(`properties[${key}]`, value);
        });

        formData.append('sections', sectionsToBundle);
        formData.append('sections_url', window.location.pathname);

        // Fetch config (matches theme's approach)
        const config = {
          method: 'POST',
          headers: {
            'X-Requested-With': 'XMLHttpRequest',
          }
        };
        config.body = formData;

        // Add to cart
        const response = await fetch(window.Shopify.routes.root + 'cart/add.js', config);
        const result = await response.json();

        // Handle error
        if (result.status || result.message) {
          console.error('[CartOfferUtils] Add failed:', result);
          return { success: false, error: result };
        }

        if (this.DEBUG) console.log('[CartOfferUtils] ✅ Product added successfully:', variantId);

        // Fetch updated cart
        const cartResponse = await fetch(window.Shopify.routes.root + 'cart.js', {
          method: 'GET',
          headers: { 'Content-Type': 'application/json' }
        });
        const cartJson = await cartResponse.json();

        // Include sections from add response
        cartJson['sections'] = result['sections'] || {};

        // Publish cart update event
        this.publishCartUpdate(cartJson);

        return { success: true, cart: cartJson };

      } catch (error) {
        console.error('[CartOfferUtils] ❌ Error adding product:', error);
        return { success: false, error };
      } finally {
        this.isProcessing = false;
      }
    },

    /**
     * Remove a product from cart by variant ID and property
     * @param {object} cartData - Current cart data
     * @param {number} variantId - The variant ID to remove
     * @param {string} propertyKey - Property key to identify the item (e.g., '_offer_type')
     * @param {string} propertyValue - Property value to match (e.g., 'buy_x_get_y')
     * @returns {Promise<{success: boolean, cart?: object, error?: any, reason?: string}>}
     */
    async removeProduct(cartData, variantId, propertyKey, propertyValue) {
      if (this.isProcessing) {
        const ready = await this.waitForIdle();
        if (!ready) return { success: false, reason: 'processing_timeout' };
      }

      if (!cartData || !cartData.items) {
        console.error('[CartOfferUtils] No cart data provided');
        return { success: false, reason: 'no_cart_data' };
      }

      if (this.DEBUG) console.log('[CartOfferUtils] Removing product:', { variantId, propertyKey, propertyValue });
      this.isProcessing = true;

      try {
        // Find item with matching variant and property
        const itemIndex = cartData.items.findIndex(item => {
          const matchesVariant = Number(item.variant_id) === Number(variantId);
          const hasProperty = item.properties &&
            String(item.properties[propertyKey]) === String(propertyValue);
          return matchesVariant && hasProperty;
        });

        if (itemIndex === -1) {
          console.warn('[CartOfferUtils] Item not found in cart');
          this.isProcessing = false;
          return { success: false, reason: 'not_found' };
        }

        const item = cartData.items[itemIndex];
        const line = item.line || (itemIndex + 1); // Line is 1-based

        if (this.DEBUG) console.log('[CartOfferUtils] Found item to remove:', { line, variantId, item });

        // Get sections to render
        let sectionsToBundle = [];
        document.documentElement.dispatchEvent(
          new CustomEvent('cart:grouped-sections', {
            bubbles: true,
            detail: { sections: sectionsToBundle }
          })
        );

        // Remove by setting quantity to 0
        const response = await fetch(window.Shopify.routes.root + 'cart/change.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            line: line,
            quantity: 0,
            sections: sectionsToBundle,
          }),
        });

        const result = await response.json();

        // Handle error
        if (result.status || result.message) {
          console.error('[CartOfferUtils] Remove failed:', result);
          return { success: false, error: result };
        }

        if (this.DEBUG) console.log('[CartOfferUtils] ✅ Product removed successfully:', variantId);

        // Publish cart update event
        this.publishCartUpdate(result);

        return { success: true, cart: result };

      } catch (error) {
        console.error('[CartOfferUtils] ❌ Error removing product:', error);
        return { success: false, error };
      } finally {
        this.isProcessing = false;
      }
    },

    /**
     * Remove all products from cart that match a property
     * @param {object} cartData - Current cart data
     * @param {string} propertyKey - Property key to identify items (e.g., '_offer_type')
     * @param {string} propertyValue - Property value to match (e.g., 'buy_x_get_y')
     * @returns {Promise<{success: boolean, cart?: object, error?: any}>}
     */
    async removeAllByProperty(cartData, propertyKey, propertyValue = null) {
      if (!cartData || !cartData.items) {
        return { success: false, reason: 'no_cart_data' };
      }

      if (this.DEBUG) console.log('[CartOfferUtils] Removing all items with property:', { propertyKey, propertyValue });

      // Find all matching items
      const itemsToRemove = [];
      cartData.items.forEach((item, index) => {
        if (item.properties && item.properties[propertyKey]) {
          // If propertyValue is null, match any value for the key
          if (propertyValue === null || String(item.properties[propertyKey]) === String(propertyValue)) {
            itemsToRemove.push({
              key: item.key,
              line: item.line || (index + 1),
              variantId: item.variant_id
            });
          }
        }
      });

      if (itemsToRemove.length === 0) {
        if (this.DEBUG) console.log('[CartOfferUtils] No items found to remove');
        return { success: true, cart: cartData };
      }

      if (this.DEBUG) console.log('[CartOfferUtils] Found items to remove:', itemsToRemove);

      // Remove all items (using update.js for batch operation)
      try {
        const updates = {};
        itemsToRemove.forEach(item => {
          if (item.key) {
            updates[item.key] = 0;
          } else if (item.variantId) {
            updates[item.variantId] = 0;
          } else {
            updates[item.line] = 0;
          }
        });

        const response = await fetch(window.Shopify.routes.root + 'cart/update.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ updates }),
        });

        const result = await response.json();

        if (this.DEBUG) console.log('[CartOfferUtils] ✅ All matching items removed');

        // Publish cart update event
        this.publishCartUpdate(result);

        return { success: true, cart: result };

      } catch (error) {
        console.error('[CartOfferUtils] ❌ Error removing items:', error);
        return { success: false, error };
      }
    },

    /**
     * Add multiple products in one request
     * @param {Array<{variantId:number, quantity:number, properties?:object}>} items
     * @returns {Promise<{success: boolean, cart?: object, error?: any, reason?: string}>}
     */
    async addProducts(items = []) {
      if (this.isProcessing) {
        const ready = await this.waitForIdle();
        if (!ready) return { success: false, reason: 'processing_timeout' };
      }

      const normalizedItems = items
        .map((item) => ({
          id: Number(item?.variantId || 0),
          quantity: Math.max(1, Number(item?.quantity || 1)),
          properties: item?.properties && typeof item.properties === 'object' ? item.properties : undefined
        }))
        .filter((item) => item.id > 0);

      if (!normalizedItems.length) {
        return { success: false, reason: 'no_items' };
      }

      this.isProcessing = true;
      try {
        const response = await fetch(window.Shopify.routes.root + 'cart/add.js', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'X-Requested-With': 'XMLHttpRequest'
          },
          body: JSON.stringify({ items: normalizedItems })
        });

        const result = await response.json();
        if (!response.ok || result?.status || result?.message) {
          return { success: false, error: result };
        }

        const cart = await this.getCartData(null, true);
        if (cart) this.publishCartUpdate(cart);
        return { success: true, cart };
      } catch (error) {
        return { success: false, error };
      } finally {
        this.isProcessing = false;
      }
    },

    /**
     * Check if a product exists in cart with specific property
     * @param {object} cartData - Current cart data
     * @param {number} variantId - The variant ID to check
     * @param {string} propertyKey - Property key to check (e.g., '_offer_type')
     * @param {string} propertyValue - Property value to match (e.g., 'buy_x_get_y')
     * @returns {boolean}
     */
    hasProductInCart(cartData, variantId, propertyKey, propertyValue) {
      if (!cartData || !cartData.items || !variantId) {
        return false;
      }

      return cartData.items.some(item => {
        const matchesVariant = Number(item.variant_id) === Number(variantId);
        const hasProperty = item.properties &&
          String(item.properties[propertyKey]) === String(propertyValue);
        return matchesVariant && hasProperty;
      });
    },

    /**
     * Publish cart update event (matches theme's event pattern)
     * @param {object} cart - Updated cart data
     */
    publishCartUpdate(cart) {
      if (this.DEBUG) console.log('[CartOfferUtils] Publishing theme:cart:change event');
      this._cartCache = cart || null;
      this._cartCacheTime = Date.now();
      
      // Dispatch theme's main cart change event (same as theme.dev.js line 2234)
      // This will trigger tier offer manager and other cart components
      document.dispatchEvent(new CustomEvent('theme:cart:change', {
        bubbles: true,
        detail: { 
          cartCount: cart.item_count,
          cart
        }
      }));
    },

    /**
     * Debounce function to prevent rapid fire calls
     * @param {Function} func - Function to debounce
     * @param {number} wait - Wait time in milliseconds
     * @returns {Function}
     */
    debounce(func, wait = 300) {
      let timeout;
      return function executedFunction(...args) {
        const later = () => {
          clearTimeout(timeout);
          func(...args);
        };
        clearTimeout(timeout);
        timeout = setTimeout(later, wait);
      };
    },

    /**
     * Normalize discount/offer title for comparison
     * @param {string} title - Title to normalize
     * @returns {string}
     */
    normalizeTitle(title) {
      if (!title) return '';
      return title.toLowerCase().replace(/\s+/g, ' ').trim();
    },

    /**
     * Get cart data (fetch if not provided)
     * @param {object} existingCart - Optional existing cart data
     * @returns {Promise<object|null>}
     */
    async getCartData(existingCart = null, forceFresh = false) {
      if (existingCart) {
        return existingCart;
      }

      const now = Date.now();
      const CACHE_TTL_MS = 800;

      // Fast path: short-lived cache for bursty offer handlers.
      if (!forceFresh && this._cartCache && now - this._cartCacheTime < CACHE_TTL_MS) {
        return this._cartCache;
      }

      // Reuse in-flight request across offer modules (including forceFresh callers)
      // to avoid N parallel cart.js requests when multiple offer components react at once.
      if (this._cartFetchPromise) {
        return await this._cartFetchPromise;
      }

      try {
        const cartUrl = forceFresh
          ? `${window.Shopify.routes.root}cart.js?_=${Date.now()}`
          : (window.Shopify.routes.root + 'cart.js');

        this._cartFetchPromise = (async () => {
          const response = await fetch(cartUrl, {
            method: 'GET',
            headers: { 'Content-Type': 'application/json' },
            cache: 'no-store',
            credentials: 'same-origin'
          });
          return await response.json();
        })();

        const cart = await this._cartFetchPromise;
        this._cartCache = cart;
        this._cartCacheTime = Date.now();
        return cart;
      } catch (error) {
        console.error('[CartOfferUtils] Error fetching cart:', error);
        return null;
      } finally {
        this._cartFetchPromise = null;
      }
    }
  };

  if (window.CartOfferUtils.DEBUG) {
    console.log('[CartOfferUtils] Utilities loaded ✓');
  }
}
